import { bitsToBytes, bytesToBits, concatBits, packBits, xorBits } from "../codec/bits";
import { concatBytes, u32be, utf8 } from "../codec/bytes";
import { FEISTEL_ROUNDS } from "../codec/constants";
import { sodiumReady } from "./sodium";

const ROUND_DOMAIN = utf8("poem-codec/v1/feistel-round\0");
const EXPAND_DOMAIN = utf8("poem-codec/v1/feistel-expand\0");
const SMALL_DOMAIN = utf8("poem-codec/v1/cycle-walk\0");

async function roundBits(
  key: Uint8Array,
  round: number,
  right: Uint8Array,
  outputBitLength: number,
): Promise<Uint8Array> {
  const sodium = await sodiumReady();
  const seed = sodium.crypto_generichash(
    64,
    concatBytes(ROUND_DOMAIN, u32be(outputBitLength), new Uint8Array([round]), packBits(right)),
    key,
  );
  const outputBytes = Math.ceil(outputBitLength / 8);
  const stream = new Uint8Array(outputBytes);
  let offset = 0;
  let counter = 0;
  while (offset < outputBytes) {
    const block = sodium.crypto_generichash(64, concatBytes(EXPAND_DOMAIN, seed, u32be(counter)), key);
    const take = Math.min(block.length, outputBytes - offset);
    stream.set(block.subarray(0, take), offset);
    offset += take;
    counter += 1;
  }
  return bytesToBits(stream).subarray(0, outputBitLength);
}

async function feistelBits(input: Uint8Array, key: Uint8Array, decrypt: boolean): Promise<Uint8Array> {
  const half = input.length / 2;
  let left: Uint8Array<ArrayBufferLike> = input.slice(0, half);
  let right: Uint8Array<ArrayBufferLike> = input.slice(half);

  if (decrypt) {
    for (let round = FEISTEL_ROUNDS - 1; round >= 0; round -= 1) {
      const previousRight = left;
      const previousLeft = xorBits(right, await roundBits(key, round, previousRight, half));
      left = previousLeft;
      right = previousRight;
    }
  } else {
    for (let round = 0; round < FEISTEL_ROUNDS; round += 1) {
      const nextLeft = right;
      const nextRight = xorBits(left, await roundBits(key, round, right, half));
      left = nextLeft;
      right = nextRight;
    }
  }
  return concatBits(left, right);
}

async function smallRound(key: Uint8Array, round: number, right: number): Promise<number> {
  const sodium = await sodiumReady();
  return sodium.crypto_generichash(1, concatBytes(SMALL_DOMAIN, new Uint8Array([round, right])), key)[0]!;
}

async function permute16(value: number, key: Uint8Array, decrypt: boolean): Promise<number> {
  let left = value >>> 8;
  let right = value & 0xff;
  if (decrypt) {
    for (let round = FEISTEL_ROUNDS - 1; round >= 0; round -= 1) {
      const previousRight = left;
      const previousLeft = right ^ (await smallRound(key, round, previousRight));
      left = previousLeft;
      right = previousRight;
    }
  } else {
    for (let round = 0; round < FEISTEL_ROUNDS; round += 1) {
      const nextLeft = right;
      const nextRight = left ^ (await smallRound(key, round, right));
      left = nextLeft;
      right = nextRight;
    }
  }
  return (left << 8) | right;
}

async function cycleWalkByte(value: number, key: Uint8Array, decrypt: boolean): Promise<number> {
  let current = value;
  do current = await permute16(current, key, decrypt);
  while (current > 0xff);
  return current;
}

export async function encryptWideBlock(plaintext: Uint8Array, key: Uint8Array): Promise<Uint8Array> {
  if (plaintext.length === 0) return new Uint8Array();
  if (plaintext.length === 1) return new Uint8Array([await cycleWalkByte(plaintext[0]!, key, false)]);
  return bitsToBytes(await feistelBits(bytesToBits(plaintext), key, false));
}

export async function decryptWideBlock(ciphertext: Uint8Array, key: Uint8Array): Promise<Uint8Array> {
  if (ciphertext.length === 0) return new Uint8Array();
  if (ciphertext.length === 1) return new Uint8Array([await cycleWalkByte(ciphertext[0]!, key, true)]);
  return bitsToBytes(await feistelBits(bytesToBits(ciphertext), key, true));
}
